import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { FormHelperText } from "@mui/material";
import { CommonInputProps, InputHelperText, Labeled } from "react-admin";
import { useInput, useResourceContext } from "ra-core";
import { EditorBuilder, isValidData, isValidJSON } from "src/components/content-builder";

export const EditorBuilderInput = (props: EditorBuilderInputProps) => {
  const {
    className,
    source,
    defaultValue,
    disabled,
    fullWidth,
    helperText,
    label,
    readOnly,
    required,
    validate,
    base64,
    ...rest
  } = props;

  const resource = useResourceContext(props);

  const validateData = () => (data: any) => {
    let value = base64 ? atob(field.value) : field.value;

    if (value == '') {
      return undefined;
    }

    if (!isValidJSON(value)) {
      return 'Incorrect output format!';
    }

    let parsedData = JSON.parse(value);

    if (required && parsedData.length == 0) {
      return 'Required';
    }

    if (!isValidData(parsedData)) {
      return 'Make sure all section are filled.';
    }
    
    return undefined;
  }
  const validators = validate && validate.length ? [validate] : validate ?? [];

  const {
    id,
    field,
    fieldState,
    formState: { isSubmitted },
  } = useInput({ ...rest, source, defaultValue, validate: [...validators as [], validateData()] });

  const { error, invalid, isTouched } = fieldState;
  const [value, setValue] = useState(field.value);

  const setVars = (value) => {
    value = value
      .replaceAll(process.env.REACT_APP_AWS_CLOUDFRONT_URL, "%cloudFrontUrl%")
      .replaceAll(process.env.REACT_APP_BASE_URL, "%baseUrl%");
    return value;
  };

  const unsetVars = (value) => {
    return value
      .replaceAll("%cloudFrontUrl%", process.env.REACT_APP_AWS_CLOUDFRONT_URL)
      .replaceAll("%baseUrl%", process.env.REACT_APP_BASE_URL);
  };

  const parseValue = (value) => {
    let parsedValue = setVars(value);

    return base64 ? btoa(unescape(encodeURIComponent(parsedValue))) : parsedValue;
  };

  const resolveValue = () => {
    let resolvedValue = base64 ? decodeURIComponent(escape(atob(value))) : value;

    return unsetVars(resolvedValue);
  };

  const handleUpdate = (value) => {
    const data = parseValue(value);

    field.onChange(data);
    field.onBlur();
  };

  return (
    <Labeled
      isRequired={required}
      label={label}
      id={`${id}-label`}
      color={fieldState?.invalid ? "error" : undefined}
      source={source}
      resource={resource}
      fullWidth={fullWidth}
      sx={{
        ".RaLabeled-label": {
          marginLeft: "14px",
        },
      }}
    >
      <Root className={className}>
        <EditorBuilder onEditorChange={handleUpdate} value={resolveValue()}/>
        <FormHelperText
          sx={{width: "100%", marginTop: "14px", paddingLeft: "14px"}}
          className={
            (isTouched || isSubmitted) && invalid
              ? "tinymce-content-input-error"
              : ""
          }
          error={(isTouched || isSubmitted) && invalid}
        >
          <InputHelperText
            touched={isTouched || isSubmitted}
            error={error?.message}
            helperText={helperText}
          />
        </FormHelperText>
      </Root>
    </Labeled>
  );
};

const PREFIX = "EditorBuilderInput";
const Root = styled("div", {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

EditorBuilderInput.defaultProps = {
  base64: false,
  disabled: false,
  required: false,
  readOnly: false,
  fullWidth: false,
  defaultValue: "",
};

export type EditorBuilderInputProps = CommonInputProps & {
  disabled?: boolean;
  readOnly?: boolean;
  base64?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  helperText?: string | React.ReactElement | false;
  className?: string;
  source: string;
};
